import { useState } from 'react';
import { useEditInput, useRemoveInput } from '../../api/hooks';
import { NOTE_EDIT_LINE, NOTE_REMOVE_LINE } from '../../lib/copy';
import { noteOverBy } from '../../lib/prose-limit';
import { Button } from '../ui/button';
import { NoteLength, noteDescribedBy } from '../ui/char-count';
import { ConfirmModal } from '../ui/confirm-modal';
import { ErrorLine } from '../ui/error-line';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';

/**
 * **Edit and Delete on one note** (2026-10-29), for an owner. Both act on the
 * whole note, every piece of a paste; the caller draws this only for a reader
 * whose writes reach `editInput`.
 */
export function NoteChangeActions({
  subjectId,
  inputId,
  prose,
}: {
  subjectId: string;
  inputId: string;
  /** The note's words as the record holds them, pieces joined. */
  prose: string;
}) {
  const [open, setOpen] = useState<'edit' | 'remove' | null>(null);
  const close = () => setOpen(null);
  return (
    <>
      <span className="person-note-actions">
        <button type="button" className="text-link" onClick={() => setOpen('edit')}>
          Edit
        </button>
        <button type="button" className="text-link" onClick={() => setOpen('remove')}>
          Delete
        </button>
      </span>
      {open === 'edit' ? (
        <EditNoteModal subjectId={subjectId} inputId={inputId} prose={prose} onClose={close} />
      ) : null}
      {open === 'remove' ? (
        <RemoveNoteModal subjectId={subjectId} inputId={inputId} onClose={close} />
      ) : null}
    </>
  );
}

function EditNoteModal({
  subjectId,
  inputId,
  prose: original,
  onClose,
}: {
  subjectId: string;
  inputId: string;
  prose: string;
  onClose: () => void;
}) {
  const edit = useEditInput(subjectId);
  const [prose, setProse] = useState(original);
  const over = noteOverBy(prose);
  const unchanged = prose.trim() === original.trim();
  const save = () => {
    if (prose.trim().length === 0 || over > 0 || unchanged || edit.isPending) return;
    edit.mutate({ inputId, prose: prose.trim() }, { onSuccess: onClose });
  };
  return (
    <Modal label="Edit note" onClose={onClose}>
      <div className="modal-head">Edit note</div>
      <textarea
        id="edit-note"
        className={over > 0 ? 'over' : undefined}
        rows={8}
        aria-label="Note"
        value={prose}
        aria-invalid={over > 0 || undefined}
        aria-describedby={noteDescribedBy('edit-note', prose)}
        onChange={(event) => setProse(event.target.value)}
      />
      <NoteLength id="edit-note" value={prose} />
      <div className="field last">
        <div className="field-note">{NOTE_EDIT_LINE}</div>
      </div>
      {edit.isError ? <ErrorLine error={edit.error} /> : null}
      <div className="modal-actions">
        <WorkingButton
          label="Save"
          workingLabel="Saving…"
          working={edit.isPending}
          disabled={prose.trim().length === 0 || over > 0 || unchanged}
          onClick={save}
        />
        <Button variant="act" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function RemoveNoteModal({
  subjectId,
  inputId,
  onClose,
}: {
  subjectId: string;
  inputId: string;
  onClose: () => void;
}) {
  const remove = useRemoveInput(subjectId);
  return (
    <ConfirmModal
      title="Delete note"
      line={NOTE_REMOVE_LINE}
      confirmLabel="Delete"
      workingLabel="Deleting…"
      danger
      working={remove.isPending}
      onConfirm={() => remove.mutate(inputId, { onSuccess: onClose })}
      dismissLabel="Cancel"
      onClose={onClose}
    >
      {remove.isError ? <ErrorLine error={remove.error} /> : null}
    </ConfirmModal>
  );
}
